import { Suspense } from 'react';
import { ErrorComponent, RouterProvider, createRouter } from '@tanstack/react-router';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider, QueryClient, QueryCache } from 'react-query';
import { authnClient } from '@frontend/auth';
import { ErrorBoundary } from '@frontend/error-boundary';
import { http } from '@frontend/fetch';
import { AlertsProvider, ThemeProvider } from '@frontend/design-system';
import '@design-system/tokens';
import Loader from '../components/Loader/Loader';
import { appConfig } from '../envconfig';
import { routeTree } from './routeTree.gen';
import './index.css';

const sharedQueryCache = new QueryCache();

const queryClient = new QueryClient({
  queryCache: sharedQueryCache,
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});

authnClient.setConfig({
  oktaClientID: appConfig.VITE_WEAVE_OKTA_CLIENT_ID,
  oktaRedirectUri: `${window.location.origin}/sign-in/callback`,
  oktaIssuer: appConfig.VITE_WEAVE_OKTA_ISSUER,
  backendAPI: appConfig.VITE_WEAVE_API,
  appSource: appConfig.VITE_WEAVE_APP_SOURCE,
});

authnClient.changeAuthMethod('okta');

// Create a new router instance
export const router = createRouter({
  routeTree,
  defaultPendingComponent: () => <Loader />,
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  context: {
    auth: authnClient,
    queryClient,
    user: undefined,
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Root element with id 'root' not found.");
}

const root = createRoot(rootElement);
http.setBaseUrl(appConfig.VITE_WEAVE_API);

root.render(
  <Suspense fallback={<Loader />}>
    <ErrorBoundary
      tier='app'
      feature='integration-dashboard.root'
      team='fe-integrations'
      fallback={({ error }) => {
        console.error('ErrorBoundary caught an error:', error);
        return <div>An error occurred: {(error as Error).message}</div>;
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider includeEmotionTheme skipAnimation={false}>
          <AlertsProvider>
            <RouterProvider router={router} context={{ auth: authnClient }} />
          </AlertsProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </Suspense>
);
